/**
 * UDISE+ Affiliation Page - Complete School Affiliation & Profile Data
 * Based on UDISE+ Portal requirements for Indian schools
 * 6 Sections: Basic Profile | Affiliation | Contact | Medium/Language | Teachers
 */
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API Helper
const api = {
  get: async (endpoint, token) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.json();
  },
  post: async (endpoint, data, token) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return res.json();
  },
  put: async (endpoint, data, token) => {
    const res = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return res.json();
  }
};

// States list for dropdown
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir', 'Ladakh'
];

// Languages list
const LANGUAGES = [
  'Hindi', 'English', 'Sanskrit', 'Urdu', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 
  'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Assamese', 'French', 'German'
];

// Minority Types
const MINORITY_TYPES = ['Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Parsi'];

// CWSN Types
const CWSN_TYPES = [
  'Blindness', 'Low-vision', 'Leprosy Cured', 'Hearing Impairment', 
  'Locomotor Disability', 'Dwarfism', 'Intellectual Disability', 
  'Mental Illness', 'Autism Spectrum Disorder', 'Cerebral Palsy', 
  'Muscular Dystrophy', 'Chronic Neurological conditions', 'Multiple Sclerosis', 
  'Speech and Language disability', 'Thalassemia', 'Hemophilia', 'Sickle Cell disease',
  'Multiple Disabilities', 'Acid Attack victim'
];

export default function UDISEAffiliationPage({ token, language = 'hi' }) {
  const [activeTab, setActiveTab] = useState('basic');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Form data for each section
  const [basicData, setBasicData] = useState({});
  const [affiliationData, setAffiliationData] = useState({});
  const [contactData, setContactData] = useState({});
  const [mediumData, setMediumData] = useState({});
  const [teacherData, setTeacherData] = useState([]);
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [newTeacher, setNewTeacher] = useState({});
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentUDISE, setShowStudentUDISE] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  
  // Tabs definition
  const tabs = [
    { id: 'basic', label: language === 'hi' ? 'बेसिक प्रोफाइल' : 'Basic Profile', icon: '🏫' },
    { id: 'affiliation', label: language === 'hi' ? 'मान्यता विवरण' : 'Affiliation', icon: '📜' },
    { id: 'contact', label: language === 'hi' ? 'संपर्क' : 'Contact', icon: '📞' },
    { id: 'medium', label: language === 'hi' ? 'माध्यम/भाषा' : 'Medium/Language', icon: '🗣️' },
    { id: 'teachers', label: language === 'hi' ? 'शिक्षक विवरण' : 'Teachers', icon: '👨‍🏫' },
    { id: 'students', label: language === 'hi' ? 'छात्र UDISE+' : 'Student UDISE+', icon: '👨‍🎓' },
  ];
  
  useEffect(() => {
    loadProfile();
  }, []);
  
  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/udise/complete-profile', token);
      setProfile(res);
      if (res.profile?.basic) setBasicData(res.profile.basic);
      if (res.profile?.affiliation) setAffiliationData(res.profile.affiliation);
      if (res.profile?.contact) setContactData(res.profile.contact);
      if (res.profile?.medium_language) setMediumData(res.profile.medium_language);
      
      // Load teachers
      const teachersRes = await api.get('/api/udise/teachers', token);
      if (teachersRes.teachers) setTeacherData(teachersRes.teachers);
      
      // Load students for UDISE+ section
      const studentsRes = await api.get('/api/students/?limit=100', token);
      if (studentsRes.students) setStudentsData(studentsRes.students);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };
  
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };
  
  const handleSave = async (section) => {
    setSaving(true);
    try {
      let endpoint = '';
      let data = {};
      
      switch (section) {
        case 'basic':
          endpoint = '/api/udise/basic-profile';
          data = basicData;
          break;
        case 'affiliation':
          endpoint = '/api/udise/affiliation';
          data = affiliationData;
          break;
        case 'contact':
          endpoint = '/api/udise/contact';
          data = contactData;
          break;
        case 'medium':
          endpoint = '/api/udise/medium-language';
          data = mediumData;
          break;
        default:
          return;
      }
      
      const res = await api.post(endpoint, data, token);
      if (res.success) {
        showToast(language === 'hi' ? 'सफलतापूर्वक सहेजा गया!' : 'Saved successfully!');
        loadProfile();
      } else {
        showToast(res.message || 'Error saving', 'error');
      }
    } catch (err) {
      showToast('Error saving: ' + err.message, 'error');
    }
    setSaving(false);
  };
  
  const handleAddTeacher = async () => {
    try {
      const res = await api.post('/api/udise/teachers', newTeacher, token);
      if (res.success) {
        showToast(language === 'hi' ? 'शिक्षक जोड़ा गया!' : 'Teacher added!');
        setNewTeacher({});
        setShowAddTeacher(false);
        loadProfile();
      }
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
  };
  
  // Form field component
  const FormField = ({ label, children, required = false, hint = null }) => (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
  
  // Input component
  const Input = ({ value, onChange, placeholder, type = 'text', ...props }) => (
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
      {...props}
    />
  );
  
  // Select component
  const Select = ({ value, onChange, options, placeholder }) => (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
    >
      <option value="">{placeholder || 'Select...'}</option>
      {options.map(opt => (
        <option key={typeof opt === 'object' ? opt.value : opt} value={typeof opt === 'object' ? opt.value : opt}>
          {typeof opt === 'object' ? opt.label : opt}
        </option>
      ))}
    </select>
  );
  
  // Multi-select for languages
  const MultiSelect = ({ value = [], onChange, options, label }) => (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <label key={opt} className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
            <input
              type="checkbox"
              checked={value?.includes(opt)}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange([...(value || []), opt]);
                } else {
                  onChange((value || []).filter(v => v !== opt));
                }
              }}
              className="rounded text-orange-500"
            />
            <span className="text-sm">{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="udise-affiliation-page">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            🏛️ UDISE+ / {language === 'hi' ? 'मान्यता विवरण' : 'School Affiliation & Profile'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'hi' 
              ? 'UDISE+ पोर्टल के लिए स्कूल की संपूर्ण जानकारी भरें' 
              : 'Fill complete school information for UDISE+ Portal'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${
            (profile?.completion?.percentage || 0) === 100 
              ? 'bg-green-100 text-green-700' 
              : (profile?.completion?.percentage || 0) >= 50 
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-red-100 text-red-700'
          }`}>
            {profile?.completion?.percentage || 0}% Complete
          </span>
          <button 
            onClick={() => window.open(`${API_URL}/api/udise/export-pdf`, '_blank')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
          >
            📄 Export PDF
          </button>
        </div>
      </div>
      
      {/* Missing Sections Alert */}
      {profile?.missing_sections?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <h3 className="font-medium text-yellow-800 flex items-center gap-2">
            ⚠️ {language === 'hi' ? 'अधूरे सेक्शन' : 'Incomplete Sections'}
          </h3>
          <p className="text-sm text-yellow-700 mt-1">
            {language === 'hi' ? 'कृपया इन सेक्शन को पूरा करें: ' : 'Please complete these sections: '}
            {profile.missing_sections.map(s => s.replace(/_/g, ' ')).join(', ')}
          </p>
        </div>
      )}
      
      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 rounded-xl whitespace-nowrap flex items-center gap-2 transition font-medium ${
              activeTab === tab.id 
                ? 'bg-orange-500 text-white shadow-md' 
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <span className="text-lg">{tab.icon}</span>
            <span>{tab.label}</span>
            {profile?.completion?.sections?.[tab.id === 'basic' ? 'basic_profile' : tab.id === 'medium' ? 'medium_language' : tab.id + '_details'] 
              ? <span className="w-5 h-5 bg-green-400 rounded-full flex items-center justify-center text-white text-xs">✓</span>
              : <span className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center text-white text-xs">○</span>}
          </button>
        ))}
      </div>
      
      {/* Content Area */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        {/* ==================== BASIC PROFILE ==================== */}
        {activeTab === 'basic' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  🏫 {language === 'hi' ? 'बेसिक स्कूल प्रोफाइल' : 'Basic School Profile'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'hi' ? 'स्कूल की मूलभूत जानकारी' : 'Basic school information'}
                </p>
              </div>
            </div>
            
            {/* School Identity */}
            <div className="bg-orange-50 rounded-xl p-5">
              <h4 className="font-semibold text-orange-800 mb-4">
                {language === 'hi' ? 'स्कूल पहचान' : 'School Identity'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="UDISE Code" required hint={language === 'hi' ? '11 अंकों का कोड' : '11-digit unique code'}>
                  <Input 
                    value={basicData.udise_code} 
                    onChange={(v) => setBasicData({...basicData, udise_code: v})}
                    placeholder="e.g., 09320101001"
                    maxLength={11}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'स्कूल का नाम (English)' : 'School Name (English)'} required>
                  <Input 
                    value={basicData.school_name} 
                    onChange={(v) => setBasicData({...basicData, school_name: v})}
                    placeholder="Enter school name"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'स्कूल का नाम (हिंदी)' : 'School Name (Hindi)'}>
                  <Input 
                    value={basicData.school_name_hindi} 
                    onChange={(v) => setBasicData({...basicData, school_name_hindi: v})}
                    placeholder="स्कूल का नाम हिंदी में"
                  />
                </FormField>
              </div>
            </div>
            
            {/* School Classification */}
            <div className="bg-blue-50 rounded-xl p-5">
              <h4 className="font-semibold text-blue-800 mb-4">
                {language === 'hi' ? 'स्कूल वर्गीकरण' : 'School Classification'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label={language === 'hi' ? 'स्कूल श्रेणी' : 'School Category'} required>
                  <Select 
                    value={basicData.school_category}
                    onChange={(v) => setBasicData({...basicData, school_category: v})}
                    options={[
                      { value: 'primary', label: 'Primary (1-5)' },
                      { value: 'upper_primary', label: 'Upper Primary (6-8)' },
                      { value: 'secondary', label: 'Secondary (9-10)' },
                      { value: 'higher_secondary', label: 'Higher Secondary (11-12)' },
                      { value: 'primary_with_upper_primary', label: 'Primary + Upper Primary (1-8)' },
                      { value: 'upper_primary_with_secondary', label: 'Upper Primary + Secondary (6-10)' },
                      { value: 'primary_to_higher_secondary', label: 'Primary to Higher Secondary (1-12)' },
                    ]}
                    placeholder={language === 'hi' ? 'चुनें' : 'Select'}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'प्रबंधन' : 'Management'} required>
                  <Select 
                    value={basicData.school_management}
                    onChange={(v) => setBasicData({...basicData, school_management: v})}
                    options={[
                      { value: 'government', label: 'Government / सरकारी' },
                      { value: 'private_aided', label: 'Private Aided / निजी सहायता प्राप्त' },
                      { value: 'private_unaided', label: 'Private Unaided / निजी गैर-सहायता प्राप्त' },
                      { value: 'central_govt', label: 'Central Govt (KVS, NVS)' },
                      { value: 'local_body', label: 'Local Body / स्थानीय निकाय' },
                      { value: 'social_welfare', label: 'Social Welfare / समाज कल्याण' },
                      { value: 'tribal_welfare', label: 'Tribal Welfare / जनजातीय कल्याण' },
                    ]}
                    placeholder={language === 'hi' ? 'चुनें' : 'Select'}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'स्कूल प्रकार' : 'School Type'} required>
                  <Select 
                    value={basicData.school_type}
                    onChange={(v) => setBasicData({...basicData, school_type: v})}
                    options={[
                      { value: 'co_ed', label: 'Co-Educational / सह-शिक्षा' },
                      { value: 'boys', label: 'Boys Only / केवल बालक' },
                      { value: 'girls', label: 'Girls Only / केवल बालिका' },
                    ]}
                    placeholder={language === 'hi' ? 'चुनें' : 'Select'}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'स्थापना वर्ष' : 'Year of Establishment'} required>
                  <Input 
                    type="number"
                    value={basicData.year_of_establishment} 
                    onChange={(v) => setBasicData({...basicData, year_of_establishment: parseInt(v)})}
                    placeholder="e.g., 1990"
                    min={1800}
                    max={new Date().getFullYear()}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'स्कूल स्थिति' : 'School Status'}>
                  <Select 
                    value={basicData.school_status}
                    onChange={(v) => setBasicData({...basicData, school_status: v})}
                    options={[
                      { value: 'active', label: 'Active / सक्रिय' },
                      { value: 'inactive', label: 'Inactive / निष्क्रिय' },
                    ]}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'ग्रामीण/शहरी' : 'Rural/Urban'}>
                  <Select 
                    value={basicData.rural_urban}
                    onChange={(v) => setBasicData({...basicData, rural_urban: v})}
                    options={[
                      { value: 'rural', label: 'Rural / ग्रामीण' },
                      { value: 'urban', label: 'Urban / शहरी' },
                    ]}
                  />
                </FormField>
              </div>
            </div>
            
            {/* Location */}
            <div className="bg-green-50 rounded-xl p-5">
              <h4 className="font-semibold text-green-800 mb-4">
                {language === 'hi' ? 'स्थान विवरण' : 'Location Details'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label={language === 'hi' ? 'राज्य' : 'State'} required>
                  <Select 
                    value={basicData.state}
                    onChange={(v) => setBasicData({...basicData, state: v})}
                    options={INDIAN_STATES}
                    placeholder={language === 'hi' ? 'राज्य चुनें' : 'Select State'}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'जिला' : 'District'} required>
                  <Input 
                    value={basicData.district} 
                    onChange={(v) => setBasicData({...basicData, district: v})}
                    placeholder={language === 'hi' ? 'जिला दर्ज करें' : 'Enter district'}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'ब्लॉक' : 'Block'} required>
                  <Input 
                    value={basicData.block} 
                    onChange={(v) => setBasicData({...basicData, block: v})}
                    placeholder={language === 'hi' ? 'ब्लॉक दर्ज करें' : 'Enter block'}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'गांव/शहर' : 'Village/City'}>
                  <Input 
                    value={basicData.village_city} 
                    onChange={(v) => setBasicData({...basicData, village_city: v})}
                    placeholder={language === 'hi' ? 'गांव/शहर का नाम' : 'Village/City name'}
                  />
                </FormField>
                
                <FormField label="Pin Code" required>
                  <Input 
                    value={basicData.pin_code} 
                    onChange={(v) => setBasicData({...basicData, pin_code: v})}
                    placeholder="e.g., 462001"
                    maxLength={6}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'क्लस्टर' : 'Cluster'}>
                  <Input 
                    value={basicData.cluster} 
                    onChange={(v) => setBasicData({...basicData, cluster: v})}
                    placeholder="Cluster name"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'वार्ड' : 'Ward'}>
                  <Input 
                    value={basicData.ward} 
                    onChange={(v) => setBasicData({...basicData, ward: v})}
                    placeholder="Ward number/name"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'अक्षांश' : 'Latitude'}>
                  <Input 
                    type="number"
                    step="0.000001"
                    value={basicData.latitude} 
                    onChange={(v) => setBasicData({...basicData, latitude: parseFloat(v)})}
                    placeholder="e.g., 23.2599"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'देशांतर' : 'Longitude'}>
                  <Input 
                    type="number"
                    step="0.000001"
                    value={basicData.longitude} 
                    onChange={(v) => setBasicData({...basicData, longitude: parseFloat(v)})}
                    placeholder="e.g., 77.4126"
                  />
                </FormField>
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button 
                onClick={() => handleSave('basic')}
                disabled={saving}
                className="px-8 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {language === 'hi' ? 'सहेज रहे हैं...' : 'Saving...'}
                  </>
                ) : (
                  <>✓ {language === 'hi' ? 'सहेजें' : 'Save Basic Profile'}</>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* ==================== AFFILIATION DETAILS ==================== */}
        {activeTab === 'affiliation' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  📜 {language === 'hi' ? 'मान्यता विवरण' : 'Affiliation Details'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'hi' ? 'बोर्ड संबद्धता और मान्यता स्थिति' : 'Board affiliation and recognition status'}
                </p>
              </div>
            </div>
            
            {/* Secondary Affiliation */}
            <div className="bg-purple-50 rounded-xl p-5">
              <h4 className="font-semibold text-purple-800 mb-4">
                {language === 'hi' ? 'Secondary (9-10) संबद्धता' : 'Secondary (9-10) Affiliation'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField label={language === 'hi' ? 'बोर्ड' : 'Board'}>
                  <Select 
                    value={affiliationData.affiliation_board_secondary}
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_board_secondary: v})}
                    options={[
                      { value: 'cbse', label: 'CBSE' },
                      { value: 'icse', label: 'ICSE' },
                      { value: 'state', label: 'State Board' },
                      { value: 'nios', label: 'NIOS' },
                      { value: 'ib', label: 'IB' },
                      { value: 'cambridge', label: 'Cambridge' },
                    ]}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'संबद्धता संख्या' : 'Affiliation Number'}>
                  <Input 
                    value={affiliationData.affiliation_number_secondary} 
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_number_secondary: v})}
                    placeholder="e.g., 1030567"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'संबद्धता से वर्ष' : 'Affiliation From Year'}>
                  <Input 
                    type="number"
                    value={affiliationData.affiliation_from_year_secondary} 
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_from_year_secondary: parseInt(v)})}
                    placeholder="e.g., 2010"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'संबद्धता तक वर्ष' : 'Affiliation Upto Year'}>
                  <Input 
                    type="number"
                    value={affiliationData.affiliation_upto_year_secondary} 
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_upto_year_secondary: parseInt(v)})}
                    placeholder="e.g., 2030"
                  />
                </FormField>
              </div>
            </div>
            
            {/* Higher Secondary Affiliation */}
            <div className="bg-indigo-50 rounded-xl p-5">
              <h4 className="font-semibold text-indigo-800 mb-4">
                {language === 'hi' ? 'Higher Secondary (11-12) संबद्धता' : 'Higher Secondary (11-12) Affiliation'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField label={language === 'hi' ? 'बोर्ड' : 'Board'}>
                  <Select 
                    value={affiliationData.affiliation_board_higher_secondary}
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_board_higher_secondary: v})}
                    options={[
                      { value: 'cbse', label: 'CBSE' },
                      { value: 'icse', label: 'ICSE' },
                      { value: 'state', label: 'State Board' },
                      { value: 'nios', label: 'NIOS' },
                      { value: 'ib', label: 'IB' },
                      { value: 'cambridge', label: 'Cambridge' },
                    ]}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'संबद्धता संख्या' : 'Affiliation Number'}>
                  <Input 
                    value={affiliationData.affiliation_number_higher_secondary} 
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_number_higher_secondary: v})}
                    placeholder="e.g., 1030567"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'संबद्धता से वर्ष' : 'Affiliation From Year'}>
                  <Input 
                    type="number"
                    value={affiliationData.affiliation_from_year_higher_secondary} 
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_from_year_higher_secondary: parseInt(v)})}
                    placeholder="e.g., 2010"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'संबद्धता तक वर्ष' : 'Affiliation Upto Year'}>
                  <Input 
                    type="number"
                    value={affiliationData.affiliation_upto_year_higher_secondary} 
                    onChange={(v) => setAffiliationData({...affiliationData, affiliation_upto_year_higher_secondary: parseInt(v)})}
                    placeholder="e.g., 2030"
                  />
                </FormField>
              </div>
            </div>
            
            {/* Recognition Status */}
            <div className="bg-teal-50 rounded-xl p-5">
              <h4 className="font-semibold text-teal-800 mb-4">
                {language === 'hi' ? 'मान्यता स्थिति' : 'Recognition Status'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['pre_primary', 'primary', 'upper_primary', 'secondary', 'higher_secondary'].map(level => (
                  <div key={level} className="flex items-center gap-4 p-3 bg-white rounded-lg">
                    <span className="font-medium text-gray-700 w-32">
                      {level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <Select 
                      value={affiliationData[`recognition_${level}`]}
                      onChange={(v) => setAffiliationData({...affiliationData, [`recognition_${level}`]: v})}
                      options={[
                        { value: 'permanent', label: 'Permanent / स्थायी' },
                        { value: 'temporary', label: 'Temporary / अस्थायी' },
                        { value: 'not_applicable', label: 'Not Applicable / लागू नहीं' },
                        { value: 'under_process', label: 'Under Process / प्रक्रियाधीन' },
                      ]}
                    />
                    <Input 
                      type="number"
                      value={affiliationData[`recognition_year_${level}`]} 
                      onChange={(v) => setAffiliationData({...affiliationData, [`recognition_year_${level}`]: parseInt(v)})}
                      placeholder="Year"
                      className="w-24"
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Special Status */}
            <div className="bg-yellow-50 rounded-xl p-5">
              <h4 className="font-semibold text-yellow-800 mb-4">
                {language === 'hi' ? 'विशेष स्थिति' : 'Special Status'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="PFMS ID / Unique Agency Code">
                  <Input 
                    value={affiliationData.pfms_id} 
                    onChange={(v) => setAffiliationData({...affiliationData, pfms_id: v})}
                    placeholder="Enter PFMS ID"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'पाठ्यक्रम' : 'Curriculum'}>
                  <Select 
                    value={affiliationData.curriculum}
                    onChange={(v) => setAffiliationData({...affiliationData, curriculum: v})}
                    options={[
                      { value: 'ncert', label: 'NCERT' },
                      { value: 'state', label: 'State' },
                      { value: 'others', label: 'Others' },
                    ]}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'वर्गीकरण' : 'Classification'}>
                  <Select 
                    value={affiliationData.classification}
                    onChange={(v) => setAffiliationData({...affiliationData, classification: v})}
                    options={[
                      { value: 'formal', label: 'Formal / औपचारिक' },
                      { value: 'special_cwsn', label: 'Special CWSN / विशेष CWSN' },
                    ]}
                  />
                </FormField>
              </div>
              
              {/* Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                  <input 
                    type="checkbox" 
                    checked={affiliationData.is_minority_school || false}
                    onChange={(e) => setAffiliationData({...affiliationData, is_minority_school: e.target.checked})}
                    className="w-5 h-5 rounded text-orange-500"
                  />
                  <div>
                    <span className="font-medium">{language === 'hi' ? 'अल्पसंख्यक विद्यालय' : 'Minority School'}</span>
                    <p className="text-xs text-gray-500">Is this a minority institution?</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                  <input 
                    type="checkbox" 
                    checked={affiliationData.is_pm_shri_school || false}
                    onChange={(e) => setAffiliationData({...affiliationData, is_pm_shri_school: e.target.checked})}
                    className="w-5 h-5 rounded text-orange-500"
                  />
                  <div>
                    <span className="font-medium">PM-SHRI School</span>
                    <p className="text-xs text-gray-500">Is this a PM-SHRI school?</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                  <input 
                    type="checkbox" 
                    checked={affiliationData.is_rte_applicable || false}
                    onChange={(e) => setAffiliationData({...affiliationData, is_rte_applicable: e.target.checked})}
                    className="w-5 h-5 rounded text-orange-500"
                  />
                  <div>
                    <span className="font-medium">RTE Applicable</span>
                    <p className="text-xs text-gray-500">Is RTE Section 12 applicable?</p>
                  </div>
                </label>
              </div>
              
              {/* Minority Type if minority school */}
              {affiliationData.is_minority_school && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label={language === 'hi' ? 'अल्पसंख्यक प्रकार' : 'Minority Type'}>
                    <Select 
                      value={affiliationData.minority_type}
                      onChange={(v) => setAffiliationData({...affiliationData, minority_type: v})}
                      options={MINORITY_TYPES}
                    />
                  </FormField>
                  <FormField label={language === 'hi' ? 'अल्पसंख्यक प्रमाणपत्र संख्या' : 'Minority Certificate Number'}>
                    <Input 
                      value={affiliationData.minority_certificate_number} 
                      onChange={(v) => setAffiliationData({...affiliationData, minority_certificate_number: v})}
                    />
                  </FormField>
                </div>
              )}
              
              {/* CWSN Types if special school */}
              {affiliationData.classification === 'special_cwsn' && (
                <div className="mt-4">
                  <FormField label={language === 'hi' ? 'CWSN प्रकार' : 'CWSN Types'}>
                    <MultiSelect 
                      value={affiliationData.cwsn_types}
                      onChange={(v) => setAffiliationData({...affiliationData, cwsn_types: v})}
                      options={CWSN_TYPES}
                    />
                  </FormField>
                </div>
              )}
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button 
                onClick={() => handleSave('affiliation')}
                disabled={saving}
                className="px-8 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {language === 'hi' ? 'सहेज रहे हैं...' : 'Saving...'}
                  </>
                ) : (
                  <>✓ {language === 'hi' ? 'सहेजें' : 'Save Affiliation Details'}</>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* ==================== CONTACT INFORMATION ==================== */}
        {activeTab === 'contact' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  📞 {language === 'hi' ? 'संपर्क जानकारी' : 'Contact Information'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'hi' ? 'स्कूल और प्रधानाचार्य की संपर्क जानकारी' : 'School and principal contact details'}
                </p>
              </div>
            </div>
            
            {/* School Contact */}
            <div className="bg-blue-50 rounded-xl p-5">
              <h4 className="font-semibold text-blue-800 mb-4">
                {language === 'hi' ? 'स्कूल संपर्क' : 'School Contact'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label={language === 'hi' ? 'स्कूल ईमेल' : 'School Email'}>
                  <Input 
                    type="email"
                    value={contactData.school_email} 
                    onChange={(v) => setContactData({...contactData, school_email: v})}
                    placeholder="school@example.com"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'स्कूल फोन' : 'School Phone'}>
                  <Input 
                    value={contactData.school_phone} 
                    onChange={(v) => setContactData({...contactData, school_phone: v})}
                    placeholder="e.g., 07552345678"
                  />
                </FormField>
                
                <FormField label="STD Code">
                  <Input 
                    value={contactData.school_std_code} 
                    onChange={(v) => setContactData({...contactData, school_std_code: v})}
                    placeholder="e.g., 0755"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'वेबसाइट' : 'Website'}>
                  <Input 
                    value={contactData.school_website} 
                    onChange={(v) => setContactData({...contactData, school_website: v})}
                    placeholder="https://www.school.edu.in"
                  />
                </FormField>
                
                <FormField label="Fax">
                  <Input 
                    value={contactData.school_fax} 
                    onChange={(v) => setContactData({...contactData, school_fax: v})}
                    placeholder="Fax number"
                  />
                </FormField>
              </div>
            </div>
            
            {/* Head of School */}
            <div className="bg-green-50 rounded-xl p-5">
              <h4 className="font-semibold text-green-800 mb-4">
                {language === 'hi' ? 'प्रधानाचार्य/प्राचार्य विवरण' : 'Head of School Details'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label={language === 'hi' ? 'नाम' : 'Name'} required>
                  <Input 
                    value={contactData.head_name} 
                    onChange={(v) => setContactData({...contactData, head_name: v})}
                    placeholder="Principal/Headmaster name"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'पदनाम' : 'Designation'} required>
                  <Select 
                    value={contactData.head_designation}
                    onChange={(v) => setContactData({...contactData, head_designation: v})}
                    options={[
                      { value: 'Principal', label: 'Principal / प्राचार्य' },
                      { value: 'Headmaster', label: 'Headmaster / प्रधानाध्यापक' },
                      { value: 'Headmistress', label: 'Headmistress / प्रधानाध्यापिका' },
                      { value: 'Director', label: 'Director / निदेशक' },
                    ]}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'मोबाइल' : 'Mobile'} required>
                  <Input 
                    value={contactData.head_mobile} 
                    onChange={(v) => setContactData({...contactData, head_mobile: v})}
                    placeholder="10-digit mobile"
                    maxLength={10}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'ईमेल' : 'Email'}>
                  <Input 
                    type="email"
                    value={contactData.head_email} 
                    onChange={(v) => setContactData({...contactData, head_email: v})}
                    placeholder="principal@school.edu.in"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'योग्यता' : 'Qualification'}>
                  <Input 
                    value={contactData.head_qualification} 
                    onChange={(v) => setContactData({...contactData, head_qualification: v})}
                    placeholder="e.g., M.A., B.Ed."
                  />
                </FormField>
              </div>
            </div>
            
            {/* Responsible Person */}
            <div className="bg-purple-50 rounded-xl p-5">
              <h4 className="font-semibold text-purple-800 mb-4">
                {language === 'hi' ? 'UDISE+ जिम्मेदार व्यक्ति' : 'UDISE+ Responsible Person'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label={language === 'hi' ? 'नाम' : 'Name'} required>
                  <Input 
                    value={contactData.responsible_person_name} 
                    onChange={(v) => setContactData({...contactData, responsible_person_name: v})}
                    placeholder="Person responsible for UDISE data"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'पदनाम' : 'Designation'} required>
                  <Input 
                    value={contactData.responsible_person_designation} 
                    onChange={(v) => setContactData({...contactData, responsible_person_designation: v})}
                    placeholder="e.g., Teacher, Clerk"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'मोबाइल' : 'Mobile'} required>
                  <Input 
                    value={contactData.responsible_person_mobile} 
                    onChange={(v) => setContactData({...contactData, responsible_person_mobile: v})}
                    placeholder="10-digit mobile"
                    maxLength={10}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'ईमेल' : 'Email'}>
                  <Input 
                    type="email"
                    value={contactData.responsible_person_email} 
                    onChange={(v) => setContactData({...contactData, responsible_person_email: v})}
                    placeholder="person@school.edu.in"
                  />
                </FormField>
              </div>
            </div>
            
            {/* SMC/SDMC Details */}
            <div className="bg-orange-50 rounded-xl p-5">
              <h4 className="font-semibold text-orange-800 mb-4">
                {language === 'hi' ? 'SMC/SDMC विवरण' : 'SMC/SDMC Details'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField label={language === 'hi' ? 'अध्यक्ष का नाम' : 'Chairman Name'}>
                  <Input 
                    value={contactData.smc_chairman_name} 
                    onChange={(v) => setContactData({...contactData, smc_chairman_name: v})}
                    placeholder="SMC Chairman"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'अध्यक्ष का मोबाइल' : 'Chairman Mobile'}>
                  <Input 
                    value={contactData.smc_chairman_mobile} 
                    onChange={(v) => setContactData({...contactData, smc_chairman_mobile: v})}
                    placeholder="Mobile number"
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'गठन तिथि' : 'Formation Date'}>
                  <Input 
                    type="date"
                    value={contactData.smc_formed_date} 
                    onChange={(v) => setContactData({...contactData, smc_formed_date: v})}
                  />
                </FormField>
                
                <FormField label={language === 'hi' ? 'सदस्य संख्या' : 'Total Members'}>
                  <Input 
                    type="number"
                    value={contactData.smc_members_count} 
                    onChange={(v) => setContactData({...contactData, smc_members_count: parseInt(v)})}
                    placeholder="e.g., 15"
                  />
                </FormField>
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button 
                onClick={() => handleSave('contact')}
                disabled={saving}
                className="px-8 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {language === 'hi' ? 'सहेज रहे हैं...' : 'Saving...'}
                  </>
                ) : (
                  <>✓ {language === 'hi' ? 'सहेजें' : 'Save Contact Information'}</>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* ==================== MEDIUM & LANGUAGE ==================== */}
        {activeTab === 'medium' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  🗣️ {language === 'hi' ? 'माध्यम और भाषाएं' : 'Medium & Languages'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'hi' ? 'शिक्षा का माध्यम और पढ़ाई जाने वाली भाषाएं' : 'Medium of instruction and languages taught'}
                </p>
              </div>
            </div>
            
            {/* Medium of Instruction */}
            <div className="bg-blue-50 rounded-xl p-5">
              <h4 className="font-semibold text-blue-800 mb-4">
                {language === 'hi' ? 'शिक्षा का माध्यम' : 'Medium of Instruction'}
              </h4>
              <div className="space-y-4">
                {[
                  { key: 'pre_primary', label: 'Pre-Primary' },
                  { key: 'primary', label: 'Primary (1-5)' },
                  { key: 'upper_primary', label: 'Upper Primary (6-8)' },
                  { key: 'secondary', label: 'Secondary (9-10)' },
                  { key: 'higher_secondary', label: 'Higher Secondary (11-12)' },
                ].map(level => (
                  <div key={level.key} className="flex items-center gap-4 p-3 bg-white rounded-lg">
                    <span className="font-medium text-gray-700 w-40">{level.label}</span>
                    <MultiSelect 
                      value={mediumData[`medium_${level.key}`]}
                      onChange={(v) => setMediumData({...mediumData, [`medium_${level.key}`]: v})}
                      options={['Hindi', 'English', 'Sanskrit', 'Urdu', 'Regional']}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Languages Taught */}
            <div className="bg-green-50 rounded-xl p-5">
              <h4 className="font-semibold text-green-800 mb-4">
                {language === 'hi' ? 'पढ़ाई जाने वाली भाषाएं' : 'Languages Taught'}
              </h4>
              <div className="space-y-4">
                {[
                  { key: 'pre_primary', label: 'Pre-Primary' },
                  { key: 'primary', label: 'Primary (1-5)' },
                  { key: 'upper_primary', label: 'Upper Primary (6-8)' },
                  { key: 'secondary', label: 'Secondary (9-10)' },
                  { key: 'higher_secondary', label: 'Higher Secondary (11-12)' },
                ].map(level => (
                  <div key={level.key} className="p-3 bg-white rounded-lg">
                    <span className="font-medium text-gray-700 block mb-2">{level.label}</span>
                    <MultiSelect 
                      value={mediumData[`languages_${level.key}`]}
                      onChange={(v) => setMediumData({...mediumData, [`languages_${level.key}`]: v})}
                      options={LANGUAGES}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Third Language */}
            <div className="bg-purple-50 rounded-xl p-5">
              <h4 className="font-semibold text-purple-800 mb-4">
                {language === 'hi' ? 'तृतीय भाषा' : 'Third Language'}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label={language === 'hi' ? 'तृतीय भाषा किस कक्षा से' : 'Third Language From Class'}>
                  <Select 
                    value={mediumData.third_language_from_class}
                    onChange={(v) => setMediumData({...mediumData, third_language_from_class: parseInt(v)})}
                    options={[1, 2, 3, 4, 5, 6, 7, 8].map(n => ({ value: n, label: `Class ${n}` }))}
                  />
                </FormField>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'hi' ? 'तृतीय भाषा विकल्प' : 'Third Language Options'}
                  </label>
                  <MultiSelect 
                    value={mediumData.third_language_options}
                    onChange={(v) => setMediumData({...mediumData, third_language_options: v})}
                    options={LANGUAGES}
                  />
                </div>
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <button 
                onClick={() => handleSave('medium')}
                disabled={saving}
                className="px-8 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    {language === 'hi' ? 'सहेज रहे हैं...' : 'Saving...'}
                  </>
                ) : (
                  <>✓ {language === 'hi' ? 'सहेजें' : 'Save Medium & Languages'}</>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* ==================== TEACHERS ==================== */}
        {activeTab === 'teachers' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  👨‍🏫 {language === 'hi' ? 'शिक्षक विवरण' : 'Teacher Details'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'hi' ? 'UDISE+ के लिए शिक्षकों की विस्तृत जानकारी' : 'Detailed teacher information for UDISE+'}
                </p>
              </div>
              <button 
                onClick={() => setShowAddTeacher(true)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
              >
                + {language === 'hi' ? 'नया शिक्षक जोड़ें' : 'Add Teacher'}
              </button>
            </div>
            
            {/* Teacher List */}
            {teacherData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <span className="text-5xl block mb-4">👨‍🏫</span>
                <p>{language === 'hi' ? 'कोई शिक्षक नहीं जोड़ा गया है' : 'No teachers added yet'}</p>
                <p className="text-sm mt-2">{language === 'hi' ? '"नया शिक्षक जोड़ें" बटन पर क्लिक करें' : 'Click "Add Teacher" button to add'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Teacher Code</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'नाम' : 'Name'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'लिंग' : 'Gender'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'योग्यता' : 'Qualification'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'मोबाइल' : 'Mobile'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'नियुक्ति प्रकार' : 'Appointment'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {teacherData.map((teacher, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-sm">{teacher.teacher_code}</td>
                        <td className="px-4 py-3 font-medium">{teacher.name}</td>
                        <td className="px-4 py-3">{teacher.gender}</td>
                        <td className="px-4 py-3 text-sm">{teacher.highest_academic_qualification}</td>
                        <td className="px-4 py-3 text-sm">{teacher.mobile}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            teacher.nature_of_appointment === 'permanent' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {teacher.nature_of_appointment}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Add Teacher Modal */}
            {showAddTeacher && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                  <div className="flex justify-between items-center p-5 border-b bg-orange-50">
                    <h3 className="text-lg font-bold text-gray-800">
                      {language === 'hi' ? 'नया शिक्षक जोड़ें' : 'Add New Teacher'}
                    </h3>
                    <button onClick={() => setShowAddTeacher(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
                  </div>
                  
                  <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
                    {/* Basic Info */}
                    <div className="bg-blue-50 rounded-xl p-5">
                      <h4 className="font-semibold text-blue-800 mb-4">{language === 'hi' ? 'मूलभूत जानकारी' : 'Basic Information'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField label="Teacher Code" required>
                          <Input 
                            value={newTeacher.teacher_code} 
                            onChange={(v) => setNewTeacher({...newTeacher, teacher_code: v})}
                            placeholder="Unique code"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'नाम (Aadhaar अनुसार)' : 'Name (As per Aadhaar)'} required>
                          <Input 
                            value={newTeacher.name} 
                            onChange={(v) => setNewTeacher({...newTeacher, name: v})}
                            placeholder="Full name"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'लिंग' : 'Gender'} required>
                          <Select 
                            value={newTeacher.gender}
                            onChange={(v) => setNewTeacher({...newTeacher, gender: v})}
                            options={[
                              { value: 'Male', label: 'Male / पुरुष' },
                              { value: 'Female', label: 'Female / महिला' },
                              { value: 'Other', label: 'Other / अन्य' },
                            ]}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'जन्म तिथि' : 'Date of Birth'} required>
                          <Input 
                            type="date"
                            value={newTeacher.dob} 
                            onChange={(v) => setNewTeacher({...newTeacher, dob: v})}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'सामाजिक वर्ग' : 'Social Category'}>
                          <Select 
                            value={newTeacher.social_category}
                            onChange={(v) => setNewTeacher({...newTeacher, social_category: v})}
                            options={[
                              { value: 'General', label: 'General / सामान्य' },
                              { value: 'SC', label: 'SC / अनुसूचित जाति' },
                              { value: 'ST', label: 'ST / अनुसूचित जनजाति' },
                              { value: 'OBC', label: 'OBC / अन्य पिछड़ा वर्ग' },
                            ]}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'नियुक्ति तिथि' : 'Date of Joining'}>
                          <Input 
                            type="date"
                            value={newTeacher.date_of_joining} 
                            onChange={(v) => setNewTeacher({...newTeacher, date_of_joining: v})}
                          />
                        </FormField>
                      </div>
                    </div>
                    
                    {/* Qualification */}
                    <div className="bg-green-50 rounded-xl p-5">
                      <h4 className="font-semibold text-green-800 mb-4">{language === 'hi' ? 'योग्यता' : 'Qualification'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField label={language === 'hi' ? 'उच्चतम शैक्षिक योग्यता' : 'Highest Academic Qualification'}>
                          <Select 
                            value={newTeacher.highest_academic_qualification}
                            onChange={(v) => setNewTeacher({...newTeacher, highest_academic_qualification: v})}
                            options={[
                              'Below Secondary', 'Secondary', 'Higher Secondary',
                              'Graduate', 'Post Graduate', 'M.Phil', 'Ph.D'
                            ]}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'उच्चतम व्यावसायिक योग्यता' : 'Highest Professional Qualification'}>
                          <Select 
                            value={newTeacher.highest_professional_qualification}
                            onChange={(v) => setNewTeacher({...newTeacher, highest_professional_qualification: v})}
                            options={[
                              'None', 'D.Ed/D.El.Ed', 'B.Ed', 'M.Ed', 'B.P.Ed', 'M.P.Ed', 'Other'
                            ]}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'नियुक्ति प्रकार' : 'Nature of Appointment'}>
                          <Select 
                            value={newTeacher.nature_of_appointment}
                            onChange={(v) => setNewTeacher({...newTeacher, nature_of_appointment: v})}
                            options={[
                              { value: 'permanent', label: 'Permanent / स्थायी' },
                              { value: 'contractual', label: 'Contractual / संविदा' },
                              { value: 'part_time', label: 'Part Time / अंशकालिक' },
                            ]}
                          />
                        </FormField>
                      </div>
                    </div>
                    
                    {/* Contact & ID */}
                    <div className="bg-purple-50 rounded-xl p-5">
                      <h4 className="font-semibold text-purple-800 mb-4">{language === 'hi' ? 'संपर्क और पहचान' : 'Contact & ID'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField label={language === 'hi' ? 'मोबाइल' : 'Mobile'} required>
                          <Input 
                            value={newTeacher.mobile} 
                            onChange={(v) => setNewTeacher({...newTeacher, mobile: v})}
                            placeholder="10-digit mobile"
                            maxLength={10}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'ईमेल' : 'Email'}>
                          <Input 
                            type="email"
                            value={newTeacher.email} 
                            onChange={(v) => setNewTeacher({...newTeacher, email: v})}
                            placeholder="teacher@school.edu.in"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'आधार नंबर' : 'Aadhaar Number'}>
                          <Input 
                            value={newTeacher.aadhaar_number} 
                            onChange={(v) => setNewTeacher({...newTeacher, aadhaar_number: v})}
                            placeholder="12-digit Aadhaar"
                            maxLength={12}
                          />
                        </FormField>
                      </div>
                    </div>
                    
                    {/* Subjects */}
                    <div className="bg-orange-50 rounded-xl p-5">
                      <h4 className="font-semibold text-orange-800 mb-4">{language === 'hi' ? 'विषय' : 'Subjects Can Teach'}</h4>
                      <MultiSelect 
                        value={newTeacher.subjects_can_teach}
                        onChange={(v) => setNewTeacher({...newTeacher, subjects_can_teach: v})}
                        options={['Hindi', 'English', 'Mathematics', 'Science', 'Social Science', 'Sanskrit', 'Computer', 'Physical Education', 'Art', 'Music']}
                      />
                    </div>
                  </div>
                  
                  <div className="p-5 border-t bg-gray-50 flex justify-end gap-3">
                    <button onClick={() => setShowAddTeacher(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">
                      {language === 'hi' ? 'रद्द करें' : 'Cancel'}
                    </button>
                    <button onClick={handleAddTeacher} className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                      {language === 'hi' ? 'शिक्षक जोड़ें' : 'Add Teacher'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* ==================== STUDENTS UDISE+ ==================== */}
        {activeTab === 'students' && (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  👨‍🎓 {language === 'hi' ? 'छात्र UDISE+ विवरण' : 'Student UDISE+ Details'}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'hi' ? 'UDISE+ के लिए छात्रों की विस्तृत जानकारी संपादित करें' : 'Edit detailed student information for UDISE+'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <input 
                  type="text"
                  placeholder={language === 'hi' ? 'छात्र खोजें...' : 'Search student...'}
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
            
            {/* Students List */}
            {studentsData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <span className="text-5xl block mb-4">👨‍🎓</span>
                <p>{language === 'hi' ? 'कोई छात्र नहीं मिला' : 'No students found'}</p>
                <p className="text-sm mt-2">{language === 'hi' ? 'Students section में छात्र जोड़ें' : 'Add students in Students section first'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'प्रवेश नं.' : 'Adm No.'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'नाम' : 'Name'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'कक्षा' : 'Class'}</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">UUID/State ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">CWSN</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">RTE</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">NCC/NSS</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{language === 'hi' ? 'कार्रवाई' : 'Action'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {studentsData
                      .filter(s => 
                        studentSearch === '' || 
                        s.name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
                        s.admission_number?.includes(studentSearch)
                      )
                      .map((student, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-sm">{student.admission_number}</td>
                        <td className="px-4 py-3 font-medium">{student.name}</td>
                        <td className="px-4 py-3 text-sm">{student.current_class_id || '-'}</td>
                        <td className="px-4 py-3 text-sm">{student.uuid_student_code || student.state_student_id || '-'}</td>
                        <td className="px-4 py-3">
                          {student.is_cwsn ? (
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">Yes</span>
                          ) : (
                            <span className="text-gray-400 text-xs">No</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {student.rte_status ? (
                            <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Yes</span>
                          ) : (
                            <span className="text-gray-400 text-xs">No</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(student.has_ncc || student.has_nss) ? (
                            <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                              {student.has_ncc && 'NCC'}{student.has_ncc && student.has_nss && '/'}{student.has_nss && 'NSS'}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button 
                            onClick={() => { setSelectedStudent(student); setShowStudentUDISE(true); }}
                            className="px-3 py-1 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                          >
                            {language === 'hi' ? 'संपादित' : 'Edit'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Student UDISE+ Edit Modal */}
            {showStudentUDISE && selectedStudent && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
                  <div className="flex justify-between items-center p-5 border-b bg-orange-50">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">
                        {language === 'hi' ? 'UDISE+ छात्र विवरण संपादित करें' : 'Edit Student UDISE+ Details'}
                      </h3>
                      <p className="text-sm text-gray-500">{selectedStudent.name} - {selectedStudent.admission_number}</p>
                    </div>
                    <button onClick={() => setShowStudentUDISE(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
                  </div>
                  
                  <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
                    {/* UUID & State ID */}
                    <div className="bg-blue-50 rounded-xl p-5">
                      <h4 className="font-semibold text-blue-800 mb-4">{language === 'hi' ? 'पहचान कोड' : 'Identity Codes'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <FormField label="UUID/Student Code">
                          <Input 
                            value={selectedStudent.uuid_student_code} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, uuid_student_code: v})}
                            placeholder="UUID or Student Code"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'राज्य छात्र ID' : 'State Student ID'}>
                          <Input 
                            value={selectedStudent.state_student_id} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, state_student_id: v})}
                            placeholder="State-specific ID"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'आधार नंबर' : 'Aadhaar Number'}>
                          <Input 
                            value={selectedStudent.aadhaar_number} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, aadhaar_number: v})}
                            placeholder="12-digit Aadhaar"
                            maxLength={12}
                          />
                        </FormField>
                      </div>
                    </div>
                    
                    {/* Academic Details */}
                    <div className="bg-green-50 rounded-xl p-5">
                      <h4 className="font-semibold text-green-800 mb-4">{language === 'hi' ? 'शैक्षणिक विवरण' : 'Academic Details'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <FormField label={language === 'hi' ? 'माध्यम' : 'Medium of Instruction'}>
                          <Select 
                            value={selectedStudent.medium}
                            onChange={(v) => setSelectedStudent({...selectedStudent, medium: v})}
                            options={['Hindi', 'English', 'Sanskrit', 'Urdu']}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'भाषा समूह' : 'Language Group'}>
                          <Input 
                            value={selectedStudent.language_group} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, language_group: v})}
                            placeholder="e.g., Hindi-English-Sanskrit"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'विषय समूह' : 'Subject Group'}>
                          <Select 
                            value={selectedStudent.subject_group}
                            onChange={(v) => setSelectedStudent({...selectedStudent, subject_group: v})}
                            options={[
                              { value: 'science', label: 'Science / विज्ञान' },
                              { value: 'commerce', label: 'Commerce / वाणिज्य' },
                              { value: 'arts', label: 'Arts / कला' },
                              { value: 'general', label: 'General / सामान्य' },
                            ]}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'स्ट्रीम' : 'Academic Stream'}>
                          <Select 
                            value={selectedStudent.stream}
                            onChange={(v) => setSelectedStudent({...selectedStudent, stream: v})}
                            options={['Science', 'Commerce', 'Arts', 'Vocational']}
                          />
                        </FormField>
                      </div>
                    </div>
                    
                    {/* Distance & Transport */}
                    <div className="bg-purple-50 rounded-xl p-5">
                      <h4 className="font-semibold text-purple-800 mb-4">{language === 'hi' ? 'दूरी और परिवहन' : 'Distance & Transport'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField label={language === 'hi' ? 'घर से स्कूल की दूरी (km)' : 'Distance from School (km)'}>
                          <Input 
                            type="number"
                            step="0.1"
                            value={selectedStudent.distance_from_school_km} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, distance_from_school_km: parseFloat(v)})}
                            placeholder="e.g., 2.5"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'परिवहन का माध्यम' : 'Mode of Transport'}>
                          <Select 
                            value={selectedStudent.mode_of_transport}
                            onChange={(v) => setSelectedStudent({...selectedStudent, mode_of_transport: v})}
                            options={[
                              { value: 'walk', label: 'Walk / पैदल' },
                              { value: 'cycle', label: 'Cycle / साइकिल' },
                              { value: 'school_bus', label: 'School Bus / स्कूल बस' },
                              { value: 'public_transport', label: 'Public Transport / सार्वजनिक परिवहन' },
                              { value: 'auto', label: 'Auto / ऑटो' },
                              { value: 'private_vehicle', label: 'Private Vehicle / निजी वाहन' },
                            ]}
                          />
                        </FormField>
                      </div>
                    </div>
                    
                    {/* CWSN & RTE */}
                    <div className="bg-teal-50 rounded-xl p-5">
                      <h4 className="font-semibold text-teal-800 mb-4">{language === 'hi' ? 'CWSN और RTE विवरण' : 'CWSN & RTE Details'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                            <input 
                              type="checkbox" 
                              checked={selectedStudent.is_cwsn || false}
                              onChange={(e) => setSelectedStudent({...selectedStudent, is_cwsn: e.target.checked})}
                              className="w-5 h-5 rounded text-orange-500"
                            />
                            <div>
                              <span className="font-medium">CWSN (दिव्यांग)</span>
                              <p className="text-xs text-gray-500">Children with Special Needs</p>
                            </div>
                          </label>
                          
                          {selectedStudent.is_cwsn && (
                            <div className="grid grid-cols-2 gap-3 pl-4">
                              <FormField label={language === 'hi' ? 'दिव्यांगता प्रकार' : 'Disability Type'}>
                                <Select 
                                  value={selectedStudent.disability_type}
                                  onChange={(v) => setSelectedStudent({...selectedStudent, disability_type: v})}
                                  options={['Visual', 'Hearing', 'Speech', 'Locomotor', 'Mental', 'Cerebral Palsy', 'Autism', 'Multiple']}
                                />
                              </FormField>
                              <FormField label={language === 'hi' ? 'दिव्यांगता %' : 'Disability %'}>
                                <Input 
                                  type="number"
                                  value={selectedStudent.disability_percentage} 
                                  onChange={(v) => setSelectedStudent({...selectedStudent, disability_percentage: parseInt(v)})}
                                  placeholder="e.g., 40"
                                  max={100}
                                />
                              </FormField>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                            <input 
                              type="checkbox" 
                              checked={selectedStudent.rte_status || false}
                              onChange={(e) => setSelectedStudent({...selectedStudent, rte_status: e.target.checked})}
                              className="w-5 h-5 rounded text-orange-500"
                            />
                            <div>
                              <span className="font-medium">RTE {language === 'hi' ? 'प्रवेश' : 'Admission'}</span>
                              <p className="text-xs text-gray-500">Right to Education Act admission</p>
                            </div>
                          </label>
                          
                          {selectedStudent.rte_status && (
                            <div className="pl-4">
                              <FormField label={language === 'hi' ? 'RTE प्रवेश वर्ष' : 'RTE Admission Year'}>
                                <Input 
                                  value={selectedStudent.rte_admission_year} 
                                  onChange={(v) => setSelectedStudent({...selectedStudent, rte_admission_year: v})}
                                  placeholder="e.g., 2020-21"
                                />
                              </FormField>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* OOSC & Gifted */}
                    <div className="bg-yellow-50 rounded-xl p-5">
                      <h4 className="font-semibold text-yellow-800 mb-4">{language === 'hi' ? 'विशेष श्रेणी' : 'Special Categories'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                            <input 
                              type="checkbox" 
                              checked={selectedStudent.is_oosc || false}
                              onChange={(e) => setSelectedStudent({...selectedStudent, is_oosc: e.target.checked})}
                              className="w-5 h-5 rounded text-orange-500"
                            />
                            <div>
                              <span className="font-medium">OOSC (Out of School Children)</span>
                              <p className="text-xs text-gray-500">{language === 'hi' ? 'पहले स्कूल छोड़ चुका था' : 'Previously dropped out'}</p>
                            </div>
                          </label>
                          
                          {selectedStudent.is_oosc && (
                            <div className="pl-4">
                              <FormField label={language === 'hi' ? 'कारण' : 'Reason'}>
                                <Input 
                                  value={selectedStudent.oosc_reason} 
                                  onChange={(v) => setSelectedStudent({...selectedStudent, oosc_reason: v})}
                                  placeholder={language === 'hi' ? 'स्कूल छोड़ने का कारण' : 'Reason for dropping out'}
                                />
                              </FormField>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                            <input 
                              type="checkbox" 
                              checked={selectedStudent.is_gifted || false}
                              onChange={(e) => setSelectedStudent({...selectedStudent, is_gifted: e.target.checked})}
                              className="w-5 h-5 rounded text-orange-500"
                            />
                            <div>
                              <span className="font-medium">{language === 'hi' ? 'प्रतिभाशाली बालक' : 'Gifted Children'}</span>
                              <p className="text-xs text-gray-500">{language === 'hi' ? 'विशेष प्रतिभा वाले छात्र' : 'Students with special talents'}</p>
                            </div>
                          </label>
                          
                          {selectedStudent.is_gifted && (
                            <div className="pl-4">
                              <FormField label={language === 'hi' ? 'प्रतिभा क्षेत्र' : 'Gifted Area'}>
                                <Select 
                                  value={selectedStudent.gifted_area}
                                  onChange={(v) => setSelectedStudent({...selectedStudent, gifted_area: v})}
                                  options={['Academic', 'Arts', 'Music', 'Sports', 'Leadership', 'Creative', 'Other']}
                                />
                              </FormField>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* NCC/NSS/Scout */}
                    <div className="bg-indigo-50 rounded-xl p-5">
                      <h4 className="font-semibold text-indigo-800 mb-4">{language === 'hi' ? 'NCC/NSS/स्काउट-गाइड' : 'NCC/NSS/Scout-Guide'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* NCC */}
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                            <input 
                              type="checkbox" 
                              checked={selectedStudent.has_ncc || false}
                              onChange={(e) => setSelectedStudent({...selectedStudent, has_ncc: e.target.checked})}
                              className="w-5 h-5 rounded text-orange-500"
                            />
                            <span className="font-medium">NCC</span>
                          </label>
                          
                          {selectedStudent.has_ncc && (
                            <div className="space-y-2 pl-4">
                              <FormField label="Wing">
                                <Select 
                                  value={selectedStudent.ncc_wing}
                                  onChange={(v) => setSelectedStudent({...selectedStudent, ncc_wing: v})}
                                  options={['Army', 'Navy', 'Air Force']}
                                />
                              </FormField>
                              <FormField label="Rank">
                                <Input 
                                  value={selectedStudent.ncc_rank} 
                                  onChange={(v) => setSelectedStudent({...selectedStudent, ncc_rank: v})}
                                  placeholder="e.g., Cadet, Lance Corporal"
                                />
                              </FormField>
                            </div>
                          )}
                        </div>
                        
                        {/* NSS */}
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                            <input 
                              type="checkbox" 
                              checked={selectedStudent.has_nss || false}
                              onChange={(e) => setSelectedStudent({...selectedStudent, has_nss: e.target.checked})}
                              className="w-5 h-5 rounded text-orange-500"
                            />
                            <span className="font-medium">NSS</span>
                          </label>
                          
                          {selectedStudent.has_nss && (
                            <div className="pl-4">
                              <FormField label={language === 'hi' ? 'NSS नामांकन संख्या' : 'NSS Enrollment No.'}>
                                <Input 
                                  value={selectedStudent.nss_enrollment_number} 
                                  onChange={(v) => setSelectedStudent({...selectedStudent, nss_enrollment_number: v})}
                                  placeholder="NSS enrollment number"
                                />
                              </FormField>
                            </div>
                          )}
                        </div>
                        
                        {/* Scout/Guide */}
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 bg-white rounded-lg cursor-pointer hover:bg-gray-50">
                            <input 
                              type="checkbox" 
                              checked={selectedStudent.has_scout_guide || false}
                              onChange={(e) => setSelectedStudent({...selectedStudent, has_scout_guide: e.target.checked})}
                              className="w-5 h-5 rounded text-orange-500"
                            />
                            <span className="font-medium">{language === 'hi' ? 'स्काउट/गाइड' : 'Scout/Guide'}</span>
                          </label>
                          
                          {selectedStudent.has_scout_guide && (
                            <div className="pl-4">
                              <FormField label={language === 'hi' ? 'रैंक' : 'Rank'}>
                                <Input 
                                  value={selectedStudent.scout_guide_rank} 
                                  onChange={(v) => setSelectedStudent({...selectedStudent, scout_guide_rank: v})}
                                  placeholder="e.g., Cub, Scout, Rover"
                                />
                              </FormField>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Previous Year Details */}
                    <div className="bg-rose-50 rounded-xl p-5">
                      <h4 className="font-semibold text-rose-800 mb-4">{language === 'hi' ? 'पिछले वर्ष का विवरण' : 'Previous Year Details'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <FormField label={language === 'hi' ? 'पिछली कक्षा' : 'Previous Class'}>
                          <Input 
                            value={selectedStudent.prev_year_class} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, prev_year_class: v})}
                            placeholder="e.g., 7th"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'परिणाम' : 'Result'}>
                          <Select 
                            value={selectedStudent.prev_year_result}
                            onChange={(v) => setSelectedStudent({...selectedStudent, prev_year_result: v})}
                            options={[
                              { value: 'pass', label: 'Pass / उत्तीर्ण' },
                              { value: 'fail', label: 'Fail / अनुत्तीर्ण' },
                              { value: 'compartment', label: 'Compartment / पूरक' },
                            ]}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'प्राप्तांक' : 'Marks Obtained'}>
                          <Input 
                            type="number"
                            value={selectedStudent.prev_year_marks_obtained} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, prev_year_marks_obtained: parseFloat(v)})}
                            placeholder="e.g., 450"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'कुल अंक' : 'Total Marks'}>
                          <Input 
                            type="number"
                            value={selectedStudent.prev_year_total_marks} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, prev_year_total_marks: parseFloat(v)})}
                            placeholder="e.g., 600"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'उपस्थिति %' : 'Attendance %'}>
                          <Input 
                            type="number"
                            value={selectedStudent.prev_year_attendance_percentage} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, prev_year_attendance_percentage: parseFloat(v)})}
                            placeholder="e.g., 85"
                            max={100}
                          />
                        </FormField>
                      </div>
                    </div>
                    
                    {/* Health Info */}
                    <div className="bg-cyan-50 rounded-xl p-5">
                      <h4 className="font-semibold text-cyan-800 mb-4">{language === 'hi' ? 'स्वास्थ्य विवरण' : 'Health Information'}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <FormField label={language === 'hi' ? 'रक्त समूह' : 'Blood Group'}>
                          <Select 
                            value={selectedStudent.blood_group}
                            onChange={(v) => setSelectedStudent({...selectedStudent, blood_group: v})}
                            options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']}
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'ऊंचाई (cm)' : 'Height (cm)'}>
                          <Input 
                            type="number"
                            value={selectedStudent.height_cm} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, height_cm: parseFloat(v)})}
                            placeholder="e.g., 145"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'वजन (kg)' : 'Weight (kg)'}>
                          <Input 
                            type="number"
                            value={selectedStudent.weight_kg} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, weight_kg: parseFloat(v)})}
                            placeholder="e.g., 35"
                          />
                        </FormField>
                        
                        <FormField label={language === 'hi' ? 'एलर्जी' : 'Allergies'}>
                          <Input 
                            value={selectedStudent.allergies} 
                            onChange={(v) => setSelectedStudent({...selectedStudent, allergies: v})}
                            placeholder={language === 'hi' ? 'यदि कोई हो' : 'If any'}
                          />
                        </FormField>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-5 border-t bg-gray-50 flex justify-end gap-3">
                    <button onClick={() => setShowStudentUDISE(false)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">
                      {language === 'hi' ? 'रद्द करें' : 'Cancel'}
                    </button>
                    <button 
                      onClick={async () => {
                        try {
                          const res = await api.put(`/api/students/${selectedStudent.id}`, selectedStudent, token);
                          if (res.success !== false) {
                            showToast(language === 'hi' ? 'छात्र अपडेट हो गया!' : 'Student updated!');
                            setShowStudentUDISE(false);
                            loadProfile();
                          }
                        } catch (err) {
                          showToast('Error: ' + err.message, 'error');
                        }
                      }}
                      className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                    >
                      {language === 'hi' ? 'सहेजें' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
